import type {DeepPartial} from '@/global';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {ItemTransitionEndpoints, ItemTransitionObject, PageTransitionsObject, TransitionBoundaryId, TransitionWindow} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {ItemTransitionType} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {createItemTransitionObject} from '@PosterWhiteboard/page/transitions/item-transition-factory';
import {PageTransitionsRenderer} from '@PosterWhiteboard/page/transitions/page-transitions-renderer.class';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import {getUniqueString} from '@Utils/string.util';
import {getTransitionWindowForSpans} from '@Libraries/item-transition.library';

/**
 * Page-level collection of background-to-background transitions, keyed by transition uid.
 * Entities are plain serialized-shape objects (`ItemTransitionObject`); all integrity logic
 * lives here, not on the entities.
 *
 * Ownership rule (matches user expectations set by other editors): a transition is owned by
 * its `fromItemUid` background. `revalidate()` — hooked into every background retile — deletes
 * transitions whose owner is gone or has no timeline successor, and rewrites `toItemUid` to
 * the owner's current successor. Delete-heal, reorder-follows-owner, and undo reconciliation
 * all fall out of that single pass.
 */
export class PageTransitions extends CommonMethods {
  public transitionsHashMap: Record<string, ItemTransitionObject> = {};
  /** Timeline selection of a boundary's transition ('' = none). Ephemeral — see PageTransitionsObject. */
  public selectedBoundaryId: TransitionBoundaryId = '';
  public page: Page;
  public readonly renderer: PageTransitionsRenderer;
  #itemIndex = new Map<string, ItemTransitionEndpoints>();

  public constructor(page: Page) {
    super();
    this.page = page;
    this.renderer = new PageTransitionsRenderer(this, page);
  }

  public hasTransitions(): boolean {
    return Object.keys(this.transitionsHashMap).length !== 0;
  }

  public toObject(): PageTransitionsObject {
    const transitionsHashMap: Record<string, ItemTransitionObject> = {};
    for (const [uid, transition] of Object.entries(this.transitionsHashMap)) {
      transitionsHashMap[uid] = {...transition};
    }
    return {transitionsHashMap, selectedBoundaryId: this.selectedBoundaryId};
  }

  /**
   * Full-state reconcile (same contract as PageItems/AudioClips.updateFromObject): entities
   * absent from the incoming hashmap are dropped, present ones are merged over the existing
   * entity and re-validated. Commits are the caller's responsibility (Page.updateFromObject).
   */
  public updateFromObject(transitionsObject: DeepPartial<PageTransitionsObject> = {}): void {
    // selectedBoundaryId is deliberately ignored: undo/history/load objects must never
    // clobber the live timeline selection (same contract as audioClips.selectedAudioItemUID).
    const {transitionsHashMap} = transitionsObject;
    if (transitionsHashMap) {
      const newTransitionsHashMap: Record<string, ItemTransitionObject> = {};
      for (const [uid, incoming] of Object.entries(transitionsHashMap)) {
        if (!incoming) {
          continue;
        }
        const existing = this.transitionsHashMap[uid];
        const validated = createItemTransitionObject(existing ? {...existing, ...incoming} : incoming);
        if (validated) {
          newTransitionsHashMap[validated.uid] = validated;
        }
      }
      this.transitionsHashMap = newTransitionsHashMap;
    }
    this.revalidate();
    // Undo/redo can change transitions while the playhead is parked inside a window —
    // re-render effects for the current time (no-op headless / before fabric init).
    this.renderer.setFrame(this.page.clock.getCurrentTime());
  }

  /**
   * Applies the ownership rule against the current background layout. Call after any
   * mutation that can change background existence or order (wired into `#retileBackgrounds`).
   * Direct mutation only — no history/redux; runs inside the calling operation's commit.
   * Returns whether anything changed.
   */
  public revalidate(): boolean {
    const successorByBoundaryId = this.#getBackgroundSuccessorMap();
    let changed = false;
    const newTransitionsHashMap: Record<string, ItemTransitionObject> = {};

    for (const [uid, transition] of Object.entries(this.transitionsHashMap)) {
      const successorUid = successorByBoundaryId.get(transition.fromItemUid);
      if (successorUid === undefined) {
        changed = true;
      } else if (transition.toItemUid !== successorUid) {
        newTransitionsHashMap[uid] = {...transition, toItemUid: successorUid};
        changed = true;
      } else {
        newTransitionsHashMap[uid] = transition;
      }
    }

    if (changed) {
      this.transitionsHashMap = newTransitionsHashMap;
    }
    this.#rebuildItemIndex();
    // Direct field clear, not clearBoundarySelection(): that would dispatch to redux, and this
    // method runs inside the calling operation's commit, which mirrors the field anyway.
    if (this.selectedBoundaryId && !successorByBoundaryId.has(this.selectedBoundaryId)) {
      this.selectedBoundaryId = '';
    }
    return changed;
  }

  /**
   * Selects the boundary on the timeline (the transition region overlay). Mutually
   * exclusive with canvas/audio/subtitle selection. Pauses and seeks to the transition
   * window's start so Space previews the transition — mirrors selectBackground's pause+seek
   * contract. No-op when the boundary doesn't exist.
   */
  public async selectBoundary(boundaryId: TransitionBoundaryId): Promise<void> {
    const successorUid = this.#getBackgroundSuccessorMap().get(boundaryId);
    if (successorUid === undefined) {
      return;
    }
    this.page.poster.clearItemSelection();
    this.page.audioClips.unselectAudioPlaylist();
    this.page.items.clearSelectedSubtitle();
    await this.page.poster.pause();
    const transition = this.getTransitionOutForItem(boundaryId);
    const window = transition ? this.getWindowForTransition(transition) : null;
    const seekTime = window?.startTime ?? this.page.items.getItem(successorUid)?.onPageStartTime;
    if (seekTime !== undefined) {
      await this.page.seek(seekTime);
    }
    this.selectedBoundaryId = boundaryId;
    this.page.poster.redux.updateSelectedTransitionBoundaryId();
  }

  public clearBoundarySelection(): void {
    if (this.selectedBoundaryId) {
      this.selectedBoundaryId = '';
      this.page.poster.redux.updateSelectedTransitionBoundaryId();
    }
  }

  public getSelectedBoundaryId(): TransitionBoundaryId {
    return this.selectedBoundaryId;
  }

  public getTransitionOutForItem(itemUid: string): ItemTransitionObject | null {
    return this.#itemIndex.get(itemUid)?.out ?? null;
  }

  public getTransitionInForItem(itemUid: string): ItemTransitionObject | null {
    return this.#itemIndex.get(itemUid)?.in ?? null;
  }

  public getTransitionForId(transitionUid: string): ItemTransitionObject | null {
    return this.transitionsHashMap[transitionUid] ?? null;
  }

  /**
   * Creates or replaces the transition on the given boundary. Replacing keeps the existing
   * uid so undo records a modify, not a delete+add. Returns null when the boundary doesn't
   * exist (last background — no successor to attach to).
   */
  public setTransitionForBoundary(boundaryId: TransitionBoundaryId, transitionData: DeepPartial<ItemTransitionObject>, {undoable = true}: {undoable?: boolean} = {}): ItemTransitionObject | null {
    const successorUid = this.#getBackgroundSuccessorMap().get(boundaryId);
    if (successorUid === undefined) {
      return null;
    }
    const existing = this.getTransitionOutForItem(boundaryId);
    const validated = createItemTransitionObject({
      ...existing,
      ...transitionData,
      uid: existing?.uid ?? getUniqueString(),
      fromItemUid: boundaryId,
      toItemUid: successorUid,
    });
    if (!validated) {
      return null;
    }
    this.transitionsHashMap = {...this.transitionsHashMap, [validated.uid]: validated};
    this.#rebuildItemIndex();
    this.page.items.refreshBackgroundVisibility();
    commitPosterMutation(this.page, {skipHistory: !undoable});
    return validated;
  }

  /**
   * Mirrors the source boundary onto EVERY boundary of the page: its transition (type,
   * duration, direction, color) replaces or creates each boundary's transition; if the source
   * boundary has none, all transitions are removed. Existing uids are kept so undo records
   * modifies, and the whole sweep commits as one history entry. Returns whether anything
   * changed — a value-identical re-apply (or clearing an already clear page) commits nothing,
   * so a no-op click can't dirty the poster or arm autosave.
   */
  public applyBoundaryTransitionToAllBoundaries(sourceBoundaryId: TransitionBoundaryId): boolean {
    const successorByBoundaryId = this.#getBackgroundSuccessorMap();
    if (!successorByBoundaryId.has(sourceBoundaryId)) {
      return false;
    }
    const sourceTransition = this.getTransitionOutForItem(sourceBoundaryId);
    const newTransitionsHashMap: Record<string, ItemTransitionObject> = {};
    if (sourceTransition) {
      for (const [boundaryId, successorUid] of successorByBoundaryId) {
        const existing = this.getTransitionOutForItem(boundaryId);
        const validated = createItemTransitionObject({
          ...sourceTransition,
          uid: existing?.uid ?? getUniqueString(),
          fromItemUid: boundaryId,
          toItemUid: successorUid,
        });
        if (validated) {
          newTransitionsHashMap[validated.uid] = validated;
        }
      }
    }
    if (this.#areTransitionsHashMapsEqual(newTransitionsHashMap, this.transitionsHashMap)) {
      return false;
    }
    this.transitionsHashMap = newTransitionsHashMap;
    this.#rebuildItemIndex();
    this.page.items.refreshBackgroundVisibility();
    commitPosterMutation(this.page);
    return true;
  }

  #areTransitionsHashMapsEqual(a: Record<string, ItemTransitionObject>, b: Record<string, ItemTransitionObject>): boolean {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) {
      return false;
    }
    return aKeys.every((uid) => {
      const other = b[uid];
      return other !== undefined && this.#areTransitionObjectsEqual(a[uid], other);
    });
  }

  #areTransitionObjectsEqual(a: ItemTransitionObject, b: ItemTransitionObject): boolean {
    if (a.uid !== b.uid || a.type !== b.type || a.fromItemUid !== b.fromItemUid || a.toItemUid !== b.toItemUid || a.duration !== b.duration) {
      return false;
    }
    const aDirection = 'direction' in a ? a.direction : undefined;
    const bDirection = 'direction' in b ? b.direction : undefined;
    if (aDirection !== bDirection) {
      return false;
    }
    const aColor = 'color' in a ? a.color : undefined;
    const bColor = 'color' in b ? b.color : undefined;
    if (aColor === undefined || bColor === undefined) {
      return aColor === bColor;
    }
    return aColor.length === bColor.length && aColor.every((channel, channelIndex) => channel === bColor[channelIndex]);
  }

  /** Removes the transition on the given boundary, if any. */
  public removeTransitionForBoundary(boundaryId: TransitionBoundaryId): void {
    const existing = this.getTransitionOutForItem(boundaryId);
    if (!existing) {
      return;
    }
    const {[existing.uid]: _removed, ...transitionsHashMap} = this.transitionsHashMap;
    this.transitionsHashMap = transitionsHashMap;
    this.#rebuildItemIndex();
    this.page.items.refreshBackgroundVisibility();
    commitPosterMutation(this.page);
  }

  /**
   * Rewrites every endpoint referencing `oldItemUid` to `newItemUid`. For operations that
   * swap a background's identity in place (background type change re-creates the item under
   * a fresh uid) — the boundary didn't move, so its transitions must survive.
   * Direct mutation only; runs inside the calling operation's commit.
   */
  public remapItemUid(oldItemUid: string, newItemUid: string): void {
    let changed = false;
    for (const [uid, transition] of Object.entries(this.transitionsHashMap)) {
      if (transition.fromItemUid === oldItemUid || transition.toItemUid === oldItemUid) {
        this.transitionsHashMap[uid] = {
          ...transition,
          fromItemUid: transition.fromItemUid === oldItemUid ? newItemUid : transition.fromItemUid,
          toItemUid: transition.toItemUid === oldItemUid ? newItemUid : transition.toItemUid,
        };
        changed = true;
      }
    }
    if (changed) {
      this.#rebuildItemIndex();
    }
  }

  /**
   * Drops every transition touching `itemUid` — the one it owns AND the one arriving into
   * it. For operations that pull a background OUT of the sequence while the item survives
   * (background bar dragged onto the item track): revalidate() alone would heal the
   * predecessor's transition onto the new adjacency, but the user dragged the blend's
   * endpoint away — the transition leaves with it instead of silently re-targeting.
   * Direct mutation only; runs inside the calling operation's commit.
   */
  public handleBackgroundLeavingSequence(itemUid: string): void {
    const remainingEntries = Object.entries(this.transitionsHashMap).filter(([, transition]) => transition.fromItemUid !== itemUid && transition.toItemUid !== itemUid);
    if (remainingEntries.length === Object.keys(this.transitionsHashMap).length) {
      return;
    }
    this.transitionsHashMap = Object.fromEntries(remainingEntries);
    this.#rebuildItemIndex();
  }

  /**
   * A split keeps the original uid on the FIRST half, so without this the out-transition
   * would sit on the new boundary in the middle of what used to be one background. Transfer
   * ownership to the second half — the original end boundary is now after it. The
   * in-transition stays correct (the first half keeps the original start boundary).
   * Direct mutation only; runs inside splitItem's commit.
   */
  public handleItemSplit(originalItemUid: string, secondHalfItemUid: string): void {
    const outTransition = this.getTransitionOutForItem(originalItemUid);
    if (!outTransition) {
      return;
    }
    this.transitionsHashMap[outTransition.uid] = {...outTransition, fromItemUid: secondHalfItemUid};
    this.#rebuildItemIndex();
  }

  /**
   * The endpoints of the transition whose window contains `time`, or null when no window
   * covers it. Lets time-resolved rendering decisions (the canvas backdrop) treat the whole
   * window as one unit instead of flipping at the boundary mid-blend.
   */
  public getActiveTransitionEndpointsAtTime(time: number): {fromItem: ItemType; toItem: ItemType} | null {
    for (const transition of Object.values(this.transitionsHashMap)) {
      const window = this.getWindowForTransition(transition);
      if (!window || time < window.startTime || time >= window.endTime) {
        continue;
      }
      const fromItem = this.page.items.getItem(transition.fromItemUid);
      const toItem = this.page.items.getItem(transition.toItemUid);
      if (fromItem && toItem) {
        return {fromItem, toItem};
      }
    }
    return null;
  }

  /**
   * The span a transition renders across (see getTransitionWindowForSpans for the clamp
   * semantics). Null when either endpoint is missing or the pair isn't temporally adjacent —
   * a mid-mutation state that must not render.
   */
  public getWindowForTransition(transition: ItemTransitionObject): TransitionWindow | null {
    const fromItem = this.page.items.getItem(transition.fromItemUid);
    const toItem = this.page.items.getItem(transition.toItemUid);
    if (!fromItem || !toItem) {
      return null;
    }
    return getTransitionWindowForSpans(
      transition.duration,
      {startTime: fromItem.onPageStartTime, endTime: fromItem.getPageEndTime()},
      {startTime: toItem.onPageStartTime, endTime: toItem.getPageEndTime()}
    );
  }

  /**
   * Whether a background must stay visible at `time` because it participates in an active
   * fade/slide window there (the incoming shows its first frame before its span; the outgoing
   * holds its last frame after). Dip-to-color never extends visibility — each side renders
   * only within its own span, beneath the color overlay.
   */
  public isItemVisibleForTransitionAtTime(itemUid: string, time: number): boolean {
    const endpoints = this.#itemIndex.get(itemUid);
    if (!endpoints) {
      return false;
    }
    return this.#doesWindowExtendVisibility(endpoints.in, time) || this.#doesWindowExtendVisibility(endpoints.out, time);
  }

  #doesWindowExtendVisibility(transition: ItemTransitionObject | null, time: number): boolean {
    if (!transition || transition.type === ItemTransitionType.DIP_TO_COLOR) {
      return false;
    }
    const window = this.getWindowForTransition(transition);
    return window !== null && time >= window.startTime && time < window.endTime;
  }

  /** Each background with a timeline successor → that successor's uid. The key set is exactly the page's valid boundary ids. */
  #getBackgroundSuccessorMap(): Map<TransitionBoundaryId, string> {
    const successorByBoundaryId = new Map<TransitionBoundaryId, string>();
    const backgrounds = this.page.items.getBackgroundsSortedByStartTime();
    for (let i = 0; i < backgrounds.length - 1; i++) {
      successorByBoundaryId.set(backgrounds[i].uid, backgrounds[i + 1].uid);
    }
    return successorByBoundaryId;
  }

  #rebuildItemIndex(): void {
    this.#itemIndex.clear();
    for (const transition of Object.values(this.transitionsHashMap)) {
      this.#getOrCreateIndexEntry(transition.fromItemUid).out = transition;
      this.#getOrCreateIndexEntry(transition.toItemUid).in = transition;
    }
  }

  #getOrCreateIndexEntry(itemUid: string): ItemTransitionEndpoints {
    let entry = this.#itemIndex.get(itemUid);
    if (!entry) {
      entry = {in: null, out: null};
      this.#itemIndex.set(itemUid, entry);
    }
    return entry;
  }
}
